import { useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  Check,
  Download,
  FileText,
  Loader2,
  Paperclip,
  Pencil,
  Trash2,
  Upload,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  INVOICE_ACCEPT_ATTR,
  MAX_INVOICE_MB,
  formatInvoiceDate,
  formatInvoiceSize,
  validateInvoiceFile,
} from "@/lib/invoices";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ComponentInvoicesDialog({
  open,
  onOpenChange,
  component,
  readOnly = false,
  onChanged,
}) {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [notes, setNotes] = useState("");
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [previewId, setPreviewId] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewType, setPreviewType] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [notesDraft, setNotesDraft] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);
  const fileInputRef = useRef(null);
  const previewUrlRef = useRef(null);

  const componentId = component?.id;

  useEffect(() => {
    if (open && componentId) {
      fetchInvoices();
      setPendingFile(null);
      setNotes("");
      setProgress(0);
    }
    if (!open) {
      resetPreview();
      setInvoices([]);
      setEditingId(null);
      setDeleteTarget(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, componentId]);

  // Release the preview object URL on unmount.
  useEffect(
    () => () => {
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    },
    []
  );

  function resetPreview() {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
    setPreviewUrl(null);
    setPreviewId(null);
    setPreviewType(null);
  }

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/components/${componentId}/invoices`);
      setInvoices(response.data || []);
    } catch (error) {
      console.error("Error fetching invoices:", error);
      toast.error("Failed to load invoices");
    } finally {
      setLoading(false);
    }
  };

  const handleFile = (file) => {
    if (!file) return;
    const problem = validateInvoiceFile(file);
    if (problem) {
      toast.error(problem);
      return;
    }
    setPendingFile(file);
    setProgress(0);
  };

  const handleUpload = async () => {
    if (!pendingFile) return;
    setUploading(true);
    setProgress(0);
    try {
      const body = new FormData();
      body.append("file", pendingFile);
      body.append("notes", notes);
      await axios.post(`${API}/components/${componentId}/invoices`, body, {
        onUploadProgress: (event) => {
          if (event.total) {
            setProgress(Math.round((event.loaded * 100) / event.total));
          }
        },
      });
      toast.success("Invoice uploaded");
      setPendingFile(null);
      setNotes("");
      setProgress(0);
      if (fileInputRef.current) fileInputRef.current.value = "";
      await fetchInvoices();
      onChanged?.();
    } catch (error) {
      console.error("Error uploading invoice:", error);
      toast.error(error.response?.data?.detail || "Failed to upload invoice");
    } finally {
      setUploading(false);
    }
  };

  const openPreview = async (invoice) => {
    resetPreview();
    setPreviewId(invoice.id);
    setPreviewLoading(true);
    try {
      const response = await axios.get(`${API}/invoices/${invoice.id}/file`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(response.data);
      previewUrlRef.current = url;
      setPreviewUrl(url);
      setPreviewType(invoice.content_type);
    } catch (error) {
      console.error("Error opening invoice:", error);
      toast.error("Could not open this invoice file");
      setPreviewId(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const downloadInvoice = async (invoice) => {
    try {
      const response = await axios.get(`${API}/invoices/${invoice.id}/file`, {
        responseType: "blob",
        params: { download: true },
      });
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = invoice.filename || "invoice";
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading invoice:", error);
      toast.error("Could not download this invoice file");
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/invoices/${deleteTarget.id}`);
      toast.success("Invoice deleted");
      if (previewId === deleteTarget.id) resetPreview();
      setDeleteTarget(null);
      await fetchInvoices();
      onChanged?.();
    } catch (error) {
      console.error("Error deleting invoice:", error);
      toast.error("Failed to delete invoice");
    } finally {
      setDeleting(false);
    }
  };

  const saveNotes = async (invoice) => {
    setSavingNotes(true);
    try {
      await axios.patch(`${API}/invoices/${invoice.id}`, { notes: notesDraft });
      toast.success("Notes updated");
      setEditingId(null);
      await fetchInvoices();
    } catch (error) {
      console.error("Error updating notes:", error);
      toast.error("Failed to update notes");
    } finally {
      setSavingNotes(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-3xl rounded-none max-h-[90vh] overflow-y-auto" data-testid="invoices-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold uppercase tracking-wider flex items-center gap-2">
              <Paperclip className="w-4 h-4" />
              Invoices
            </DialogTitle>
            <DialogDescription>
              {component ? (
                <>
                  <span className="font-mono text-xs mr-2">{component.sku}</span>
                  {component.name} — PDF, JPG or PNG scans of supplier invoices.
                </>
              ) : (
                "Supplier invoice scans"
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {!readOnly && (
              <div className="form-section">
                <div className="form-section-header">Add Invoice</div>
                <div className="form-section-content space-y-3">
                  <div className="flex flex-col md:flex-row gap-3">
                    <div className="flex-1 space-y-2">
                      <Label htmlFor="invoice-file">File (PDF, JPG, PNG — max {MAX_INVOICE_MB} MB)</Label>
                      <Input
                        id="invoice-file"
                        ref={fileInputRef}
                        type="file"
                        accept={INVOICE_ACCEPT_ATTR}
                        className="rounded-none"
                        onChange={(e) => handleFile(e.target.files?.[0])}
                        data-testid="invoice-file-input"
                      />
                    </div>
                    <div className="flex-1 space-y-2">
                      <Label htmlFor="invoice-notes">Notes (optional)</Label>
                      <Input
                        id="invoice-notes"
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder="e.g. Invoice 2024-118, paid"
                        className="rounded-none"
                        data-testid="invoice-notes-input"
                      />
                    </div>
                  </div>
                  {uploading && (
                    <div className="space-y-1">
                      <Progress value={progress} className="rounded-none h-2" />
                      <p className="text-xs text-muted-foreground">Uploading… {progress}%</p>
                    </div>
                  )}
                  <div className="flex items-center gap-3">
                    <Button
                      type="button"
                      onClick={handleUpload}
                      disabled={!pendingFile || uploading}
                      className="rounded-none uppercase text-xs tracking-wider"
                      data-testid="invoice-upload-btn"
                    >
                      {uploading ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Upload className="w-4 h-4 mr-2" />
                      )}
                      {uploading ? "Uploading" : "Upload Invoice"}
                    </Button>
                    {pendingFile && !uploading && (
                      <span className="text-xs text-muted-foreground">
                        {pendingFile.name} · {formatInvoiceSize(pendingFile.size)}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* File list */}
              <div className="border border-border">
                <div className="border-b border-border px-3 py-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Attached files ({invoices.length})
                </div>
                <div className="max-h-[320px] overflow-y-auto divide-y divide-border">
                  {loading ? (
                    <div className="p-4 text-sm text-muted-foreground">Loading invoices…</div>
                  ) : invoices.length === 0 ? (
                    <div className="p-6 text-center">
                      <FileText className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                      <p className="text-sm text-muted-foreground">No invoice attached yet</p>
                    </div>
                  ) : (
                    invoices.map((invoice) => (
                      <div
                        key={invoice.id}
                        className={`p-3 space-y-2 ${previewId === invoice.id ? "bg-muted/40" : ""}`}
                        data-testid={`invoice-row-${invoice.id}`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <button
                            type="button"
                            onClick={() => openPreview(invoice)}
                            className="flex-1 text-left"
                          >
                            <p className="text-sm font-medium break-all">{invoice.filename}</p>
                            <p className="text-xs text-muted-foreground">
                              {formatInvoiceSize(invoice.size)} · {formatInvoiceDate(invoice.uploaded_at)}
                              {invoice.uploaded_by ? ` · ${invoice.uploaded_by}` : ""}
                            </p>
                          </button>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              title="Download"
                              onClick={() => downloadInvoice(invoice)}
                              data-testid={`invoice-download-${invoice.id}`}
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                            {!readOnly && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  title="Edit notes"
                                  onClick={() => {
                                    setEditingId(invoice.id);
                                    setNotesDraft(invoice.notes || "");
                                  }}
                                >
                                  <Pencil className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  title="Delete"
                                  onClick={() => setDeleteTarget(invoice)}
                                  data-testid={`invoice-delete-${invoice.id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </div>

                        {editingId === invoice.id ? (
                          <div className="space-y-2">
                            <Textarea
                              value={notesDraft}
                              onChange={(e) => setNotesDraft(e.target.value)}
                              rows={2}
                              className="rounded-none text-sm"
                              placeholder="Notes for this invoice"
                            />
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                className="rounded-none uppercase text-xs tracking-wider"
                                disabled={savingNotes}
                                onClick={() => saveNotes(invoice)}
                              >
                                <Check className="w-3 h-3 mr-1" />
                                Save
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="rounded-none uppercase text-xs tracking-wider"
                                onClick={() => setEditingId(null)}
                              >
                                <X className="w-3 h-3 mr-1" />
                                Cancel
                              </Button>
                            </div>
                          </div>
                        ) : (
                          invoice.notes && (
                            <p className="text-xs text-muted-foreground whitespace-pre-wrap">
                              {invoice.notes}
                            </p>
                          )
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Preview */}
              <div className="border border-border min-h-[320px] flex flex-col">
                <div className="border-b border-border px-3 py-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Preview
                </div>
                <div className="flex-1 flex items-center justify-center bg-muted/20 p-2">
                  {previewLoading ? (
                    <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                  ) : !previewUrl ? (
                    <p className="text-sm text-muted-foreground text-center px-4">
                      Select a file to preview it here
                    </p>
                  ) : previewType === "application/pdf" ? (
                    <iframe
                      src={previewUrl}
                      title="Invoice preview"
                      className="w-full h-[280px] border-0"
                    />
                  ) : (
                    <img
                      src={previewUrl}
                      alt="Invoice preview"
                      className="max-h-[280px] max-w-full object-contain"
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="rounded-none">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Invoice</AlertDialogTitle>
            <AlertDialogDescription>
              Delete "{deleteTarget?.filename}"? This removes the file permanently and cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="rounded-none bg-destructive"
              data-testid="confirm-invoice-delete"
            >
              {deleting ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
